/**
 * Admin/Manager super-metrics panel + Internal Chat (Telegram-like layout).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Activity, AlertOctagon, Award, Building, CheckCircle2, MessageSquare,
  RefreshCw, School, Search, Send, ShieldCheck, Trophy, Users,
} from "lucide-react";
import { API } from "../../lib/api";
import { useDrilldown } from "./DrilldownContext";

// ─── Admin Metrics (poin 18) ───
export function AdminMetricsPanel() {
  const [m, setM] = useState(null);
  const { openDrilldown } = useDrilldown();
  useEffect(() => {
    axios.get(`${API}/sales/admin-metrics`).then((r) => setM(r.data)).catch(() => setM(null));
  }, []);
  if (!m) return null;
  const tiles = [
    { icon: Activity, label: "TOTAL ACTIVITIES", value: (m.total_activities ?? 0).toLocaleString("id-ID"), sub: "Semua log aktivitas (meeting & non-meeting)",
      drill: { title: "Total Activities (Semua Aktivitas)", metric: "activities_total" } },
    { icon: Activity, label: "TOTAL MEETINGS", value: (m.total_meetings ?? 0).toLocaleString("id-ID"), sub: "Hanya aktivitas meeting (tanpa signing)", variant: "sekolah",
      drill: { title: "Total Meetings (Aktivitas Meeting)", metric: "activities_by_stage", value: "Qualified Meetings" } },
    { icon: CheckCircle2, label: "QUALIFIED MEETINGS", value: m.qualified_meetings.toLocaleString("id-ID"), sub: "Kegiatan = Qualified Meetings", variant: "teachcast",
      drill: { title: "Qualified Meetings", metric: "activities_qualified" } },
    { icon: AlertOctagon, label: "REJECTED SCHOOLS", value: `${m.rejected_schools.toLocaleString("id-ID")} · ${(m.rejected_students || 0).toLocaleString("id-ID")} siswa`, sub: "Lost / Churn account (sekolah · est. siswa)", variant: "eft",
      drill: { title: "Rejected Schools (Lost + Churn)", metric: "leads_lost" } },
    { icon: ShieldCheck, label: "QUALIFIED SCHOOLS", value: `${m.qualified_schools.toLocaleString("id-ID")} · ${(m.qualified_students || 0).toLocaleString("id-ID")} siswa`, sub: "Tuition >500K + Native + TeachCast (sekolah · est. siswa)",
      drill: { title: "Qualified Schools", metric: "leads_qualified" } },
    { icon: Award, label: "ACCOUNT SIGN + STUDENT", value: `${m.account_sign_count.toLocaleString("id-ID")} · ${m.account_sign_students.toLocaleString("id-ID")}`, sub: "Signed contracts · est students", variant: "teachcast",
      drill: { title: "Account Sign (New + Existing)", metric: "leads_won" } },
    { icon: Building, label: "UNIQUE SCHOOLS", value: `${m.unique_schools.toLocaleString("id-ID")} · ~${m.unique_school_students_est.toLocaleString("id-ID")}`, sub: "Sekolah distinct · est total siswa", variant: "sekolah",
      drill: { title: "Sekolah Unik (Approached)", metric: "activities_unique_schools" } },
  ];
  return (
    <div data-testid="admin-metrics">
      <div className="section-title">🌟 Super Metrics (Admin/Manager Only) · klik tile untuk drill-down</div>
      <div className="grid kpi-grid" style={{marginBottom: 14}}>
        {tiles.map((t, i) => (
          <div key={i} className={`kpi-card clickable ${t.variant || ""}`}
            data-testid={`am-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => openDrilldown(t.drill)}
            role="button" tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openDrilldown(t.drill)}>
            <div className="kpi-icon"><t.icon size={20}/></div>
            <div className="label">{t.label}</div>
            <div className="value" style={{fontSize: t.value.length > 12 ? 22 : 28}}>{t.value}</div>
            <div className="sub">{t.sub}</div>
            <div className="kpi-hint">Klik untuk drill-down →</div>
          </div>
        ))}
      </div>
      {m.rejected_reasons && m.rejected_reasons.length > 0 && (
        <div className="card" style={{padding: 18}}>
          <h3 className="card-title">Rejected Reasons Analytics · klik badge untuk lihat lead-nya</h3>
          <div style={{display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8}}>
            {m.rejected_reasons.map((r, i) => (
              <button key={i} className="badge"
                onClick={() => openDrilldown({ title: `Rejected · Reason: ${r.code}`, metric: "leads_lost" })}
                data-testid={`am-reason-${r.code}`}
                style={{background: "#FDEAEA", color: "#c0392b", padding: "5px 12px", fontWeight: 700, fontSize: 12,
                          border: "1px solid #f5c6c6", cursor: "pointer"}}>
                {r.code} · {r.count}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Chat View (Telegram-like 2-pane) ───
export function ChatView({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [activeUser, setActiveUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(false);
  const messagesRef = useRef(null);

  const loadUsers = useCallback(async () => {
    try { const r = await axios.get(`${API}/chat/users`); setUsers(r.data.users); } catch {}
  }, []);
  const loadMessages = useCallback(async (uname) => {
    try { const r = await axios.get(`${API}/chat/messages`, { params: { with_user: uname } });
      setMessages(r.data.messages); } catch {}
  }, []);

  useEffect(() => { loadUsers(); const id = setInterval(loadUsers, 10000); return () => clearInterval(id); }, [loadUsers]);
  useEffect(() => {
    if (!activeUser) return;
    loadMessages(activeUser.username);
    const id = setInterval(() => loadMessages(activeUser.username), 4000);
    return () => clearInterval(id);
  }, [activeUser, loadMessages]);
  // Scroll only the internal .chat-messages container — never the whole page.
  // (Old code used bottomRef.scrollIntoView which scrolls the nearest scrollable
  //  ancestor, sometimes the window itself → topbar got pushed off-screen after
  //  sending a message.)
  useEffect(() => {
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!activeUser || !text.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/chat/messages`, { to_user: activeUser.username, text: text.trim() });
      setText(""); await loadMessages(activeUser.username);
    } finally { setSending(false); }
  };

  const filtered = users.filter((u) =>
    !search || u.username.toLowerCase().includes(search.toLowerCase()) ||
    (u.name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="chat-shell" data-testid="chat-view">
      <aside className="chat-list">
        <div className="chat-list-head">
          <MessageSquare size={16} color="var(--primary)"/>
          <b style={{color: "var(--navy)"}}>Chat</b>
          <span style={{marginLeft: "auto", fontSize: 11, color: "var(--muted)"}}>{users.length} kontak</span>
        </div>
        <div style={{padding: "10px 12px", borderBottom: "1px solid var(--border-2)", background: "#fff"}}>
          <div style={{position: "relative"}}>
            <Search size={12} style={{position: "absolute", left: 10, top: 11, color: "var(--muted)"}}/>
            <input className="form-input" placeholder="Cari user..." value={search}
              onChange={(e) => setSearch(e.target.value)} style={{padding: "8px 10px 8px 28px", fontSize: 12}}
              data-testid="chat-search" />
          </div>
        </div>
        <div style={{overflowY: "auto", flex: 1}}>
          {filtered.length === 0 && <div className="empty" style={{padding: 16, fontSize: 12}}>Tidak ada user.</div>}
          {filtered.map((u) => (
            <button key={u.username}
              className={`chat-user-row ${activeUser?.username === u.username ? "active" : ""}`}
              onClick={() => setActiveUser(u)} data-testid={`chat-user-${u.username}`}>
              <div className="chat-avatar">{(u.name || u.username).slice(0, 2).toUpperCase()}</div>
              <div style={{flex: 1, minWidth: 0}}>
                <div style={{display: "flex", justifyContent: "space-between", gap: 6}}>
                  <b style={{fontSize: 13, color: "var(--navy)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{u.name || u.username}</b>
                  {u.unread > 0 && <span className="chat-unread-dot">{u.unread}</span>}
                </div>
                <span style={{fontSize: 11, color: "var(--muted)", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                  {u.last_message || `@${u.username} · ${u.role}`}
                </span>
              </div>
            </button>
          ))}
        </div>
      </aside>
      <section className="chat-thread">
        {!activeUser ? (
          <div className="empty" style={{flex: 1, display: "grid", placeItems: "center", color: "var(--muted)"}}>
            <div style={{textAlign: "center"}}>
              <MessageSquare size={48} color="var(--border-2)"/>
              <p style={{marginTop: 14}}>Pilih kontak untuk mulai chat</p>
            </div>
          </div>
        ) : (<>
          <div className="chat-thread-head">
            <div className="chat-avatar">{(activeUser.name || activeUser.username).slice(0, 2).toUpperCase()}</div>
            <div>
              <b style={{color: "var(--navy)"}}>{activeUser.name || activeUser.username}</b>
              <div style={{fontSize: 11, color: "var(--muted)"}}>@{activeUser.username} · {activeUser.role}</div>
            </div>
          </div>
          <div className="chat-messages" ref={messagesRef}>
            {messages.length === 0 && <div className="empty" style={{padding: 30}}>Belum ada pesan. Mulai percakapan!</div>}
            {messages.map((m) => {
              const mine = (m.from_user || "").toLowerCase() === (currentUser || "").toLowerCase();
              return (
                <div key={m.id} className={`chat-bubble ${mine ? "mine" : "theirs"}`} data-testid={`chat-bubble-${m.id}`}>
                  <span>{m.text}</span>
                  <span className="chat-time">
                    {new Date(m.ts).toLocaleTimeString("id-ID", {hour: "2-digit", minute: "2-digit"})}
                    {mine && (
                      <span title={m.read ? "Sudah dibaca" : "Terkirim"}
                        style={{ marginLeft: 6, fontSize: 11,
                                  color: m.read ? "#7BD0FF" : "rgba(255,255,255,.55)" }}
                        data-testid={`chat-status-${m.id}`}>
                        {m.read ? "✓✓" : "✓"}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="chat-compose">
            <input className="form-input" placeholder="Ketik pesan..." value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
              disabled={sending} data-testid="chat-input" />
            <button className="ai-send" onClick={send} disabled={sending || !text.trim()} data-testid="chat-send">
              <Send size={14}/>
            </button>
          </div>
        </>)}
      </section>
    </div>
  );
}
